import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, X, ExternalLink, Cpu, Zap } from "lucide-react";

/**
 * Custom Brain Wizard — 4 steps to plug in any OpenAI-compatible endpoint.
 *
 *   1. Pick preset  — Fireworks / Together / Groq / Custom
 *   2. Model + Key  — model slug + API key
 *   3. Verify       — round-trip 1 chat completion
 *   4. Configure    — display name, cost, context; save
 */
export default function StudioCustomBrain() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState(null);
  const [modelSlug, setModelSlug] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [customBaseUrl, setCustomBaseUrl] = useState("");
  const [verifyState, setVerifyState] = useState({ status: "idle", msg: "" });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    context: 131072,
    cost_in: 0.9,
    cost_out: 0.9,
  });

  useEffect(() => {
    api.get("/custom_brains/presets").then(({ data }) => {
      setPresets(data.presets || []);
    });
  }, []);

  const pickPreset = (p) => {
    setPreset(p);
    setModelSlug(p.models?.[0]?.slug || "");
    setCustomBaseUrl(p.base_url || "");
    setForm((f) => ({
      ...f,
      name: p.id === "custom" ? "" : p.name,
      context: p.models?.[0]?.context || p.context_default,
      cost_in: p.models?.[0]?.cost_in ?? p.cost_in_default,
      cost_out: p.models?.[0]?.cost_out ?? p.cost_out_default,
    }));
    setStep(2);
  };

  const onModelPick = (slug) => {
    setModelSlug(slug);
    const m = (preset?.models || []).find((x) => x.slug === slug);
    if (m) {
      setForm((f) => ({
        ...f,
        name: preset.name + " · " + m.label,
        context: m.context,
        cost_in: m.cost_in,
        cost_out: m.cost_out,
      }));
    }
  };

  const baseUrl = () => (preset?.id === "custom" ? customBaseUrl.trim() : preset?.base_url || "").replace(/\/+$/, "");

  const verify = async () => {
    setVerifyState({ status: "checking", msg: "Sending probe request…" });
    try {
      const r = await api.post("/custom_brains/verify", {
        base_url: baseUrl(),
        model_slug: modelSlug.trim(),
        api_key: apiKey.trim(),
      });
      if (r.data.ok) {
        setVerifyState({ status: "ok", msg: r.data.reply || "Endpoint responded successfully." });
      } else {
        setVerifyState({ status: "fail", msg: r.data.error || "Verification failed" });
      }
    } catch (e) {
      setVerifyState({ status: "fail", msg: e.response?.data?.detail || e.message });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/custom_brains", {
        name: form.name.trim() || `${preset.name} brain`,
        family: preset.family,
        base_url: baseUrl(),
        model_slug: modelSlug.trim(),
        api_key: apiKey.trim(),
        context: form.context,
        cost_in: form.cost_in,
        cost_out: form.cost_out,
      });
      nav("/studio", { state: { toast: `${preset.name} brain saved. Available in Compose now.` } });
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const stepPill = (n, label) => (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 font-heading font-bold text-xs uppercase tracking-widest ${step === n ? "bg-slate-900 text-white border-slate-900" : n < step ? "bg-orange-200 border-slate-900 text-slate-900" : "bg-white border-slate-300 text-slate-500"}`}>
      {n < step ? <Check size={12} strokeWidth={3} /> : <span className="w-4 text-center">{n}</span>}
      {label}
    </div>
  );

  const canAdvance =
    (step === 1 && !!preset) ||
    (step === 2 && !!modelSlug.trim() && !!apiKey.trim() && !!baseUrl()) ||
    (step === 3 && verifyState.status === "ok") ||
    step === 4;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/studio" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold" data-testid="cb-back-to-studio">
          <ArrowLeft size={14} strokeWidth={2.5} /> Studio
        </Link>
      </div>
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-3">
          🔧 Custom Brain
        </div>
        <h1 className="font-heading text-4xl font-bold" data-testid="cb-title">Bring your own endpoint</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Plug in any OpenAI-compatible chat endpoint — Fireworks, Together, Groq, or your own vLLM/Ollama server —
          and use it everywhere on AiMedley.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="cb-steps">
        {stepPill(1, "Pick provider")}
        {stepPill(2, "Model & Key")}
        {stepPill(3, "Verify")}
        {stepPill(4, "Save")}
      </div>

      {step === 1 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="cb-presets-grid">
          {presets.map((p) => (
            <button
              key={p.id}
              onClick={() => pickPreset(p)}
              className={`brut-card p-5 text-left hover:-translate-y-1 transition-transform relative overflow-hidden ${preset?.id === p.id ? "bg-orange-50" : "bg-white"}`}
              style={{ boxShadow: preset?.id === p.id ? "6px 6px 0 #EA580C" : "4px 4px 0 #F59E0B" }}
              data-testid={`cb-preset-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="w-12 h-12 rounded-xl border-2 border-slate-900 bg-orange-100 flex items-center justify-center text-3xl">
                  {p.avatar}
                </div>
                {p.supports_fine_tune && (
                  <span className="text-[10px] font-heading font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-900 text-white inline-flex items-center gap-1">
                    <Zap size={9} strokeWidth={3} /> Fine-tune
                  </span>
                )}
              </div>
              <div className="mt-3 font-heading font-bold text-lg">{p.name}</div>
              <div className="text-xs text-slate-600 mt-1">
                {p.id === "custom"
                  ? "vLLM, Ollama, or any endpoint that speaks the OpenAI Chat Completions API."
                  : `${p.models?.length || 0} popular models pre-configured • base URL and defaults filled in.`}
              </div>
              {p.key_url && (
                <div className="mt-3 text-[11px] text-slate-500 inline-flex items-center gap-1 font-mono">
                  Get key at
                  <span className="underline">{new URL(p.key_url).hostname}</span>
                  <ExternalLink size={9} />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {step === 2 && preset && (
        <div className="space-y-4">
          <div className="brut-card p-4">
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500 mb-1">Provider</div>
            <div className="font-heading font-bold text-lg inline-flex items-center gap-2">
              <span className="text-2xl">{preset.avatar}</span> {preset.name}
            </div>
          </div>

          {preset.id === "custom" && (
            <Field label="Base URL" required>
              <input
                className="brut-input w-full font-mono"
                placeholder="https://your-endpoint.example.com/v1"
                value={customBaseUrl}
                onChange={(e) => setCustomBaseUrl(e.target.value)}
                data-testid="cb-base-url"
              />
              <p className="text-xs text-slate-500 mt-1">Must accept POST /chat/completions with an OpenAI-shaped body.</p>
            </Field>
          )}

          <Field label="Model" required>
            {preset.models && preset.models.length > 0 ? (
              <div className="space-y-2">
                <select
                  className="brut-input w-full font-mono"
                  value={preset.models.find((m) => m.slug === modelSlug) ? modelSlug : "__custom__"}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") return;
                    onModelPick(e.target.value);
                  }}
                  data-testid="cb-model-select"
                >
                  {preset.models.map((m) => (
                    <option key={m.slug} value={m.slug}>{m.label} — {m.slug}</option>
                  ))}
                  <option value="__custom__">Enter a custom slug…</option>
                </select>
                <input
                  className="brut-input w-full font-mono"
                  placeholder="Or paste a model slug manually"
                  value={modelSlug}
                  onChange={(e) => setModelSlug(e.target.value)}
                  data-testid="cb-model-slug"
                />
              </div>
            ) : (
              <input
                className="brut-input w-full font-mono"
                placeholder="e.g. llama-3.3-70b-instruct"
                value={modelSlug}
                onChange={(e) => setModelSlug(e.target.value)}
                data-testid="cb-model-slug"
              />
            )}
          </Field>

          <Field label="API key" required>
            <input
              className="brut-input w-full font-mono"
              type="password"
              placeholder={preset.key_prefix ? `${preset.key_prefix}...` : "sk-..."}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="cb-api-key"
              autoComplete="off"
            />
            {preset.key_url && (
              <p className="text-xs text-slate-500 mt-1">
                Get one at{" "}
                <a className="underline" href={preset.key_url} target="_blank" rel="noreferrer">
                  {new URL(preset.key_url).hostname}
                </a>
                {" — "}stored encrypted per user.
              </p>
            )}
          </Field>
        </div>
      )}

      {step === 3 && preset && (
        <div className="brut-card p-6 space-y-3 text-center">
          <div className="text-4xl">{preset.avatar}</div>
          <div className="font-heading text-xl font-bold">Verify your endpoint</div>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            We'll send a tiny <span className="font-mono">ping</span> to your endpoint (max 8 tokens) to confirm the key and model slug both work.
            Nothing is stored yet.
          </p>
          <div className="text-[11px] text-slate-500 font-mono break-all bg-slate-50 border border-slate-200 rounded p-2">
            POST {baseUrl()}/chat/completions<br />
            model: {modelSlug}
          </div>
          <button
            onClick={verify}
            disabled={verifyState.status === "checking"}
            className="brut-btn text-sm"
            data-testid="cb-verify-btn"
          >
            {verifyState.status === "checking" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={3} />}
            {" "}Send probe
          </button>
          {verifyState.status !== "idle" && (
            <div
              className={`text-sm px-3 py-2 rounded-lg border-2 text-left ${
                verifyState.status === "ok" ? "bg-green-50 border-green-300 text-green-800"
                : verifyState.status === "fail" ? "bg-red-50 border-red-300 text-red-800"
                : "bg-slate-50 border-slate-200 text-slate-600"
              }`}
              data-testid="cb-verify-result"
            >
              {verifyState.status === "ok"
                ? <><Check size={12} className="inline mr-1" strokeWidth={3} />{verifyState.msg}</>
                : verifyState.status === "fail"
                ? <><X size={12} className="inline mr-1" strokeWidth={3} />{verifyState.msg}</>
                : verifyState.msg}
            </div>
          )}
        </div>
      )}

      {step === 4 && preset && (
        <div className="brut-card p-4 space-y-4">
          <Field label="Display name" required>
            <input
              className="brut-input w-full"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="cb-name"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Context length">
              <input
                className="brut-input w-full"
                type="number"
                value={form.context}
                onChange={(e) => setForm((f) => ({ ...f, context: parseInt(e.target.value || "0") }))}
                data-testid="cb-context"
              />
            </Field>
            <Field label="Cost in ($/1M tok)">
              <input
                className="brut-input w-full"
                type="number"
                step="0.01"
                value={form.cost_in}
                onChange={(e) => setForm((f) => ({ ...f, cost_in: parseFloat(e.target.value || "0") }))}
                data-testid="cb-cost-in"
              />
            </Field>
            <Field label="Cost out ($/1M tok)">
              <input
                className="brut-input w-full"
                type="number"
                step="0.01"
                value={form.cost_out}
                onChange={(e) => setForm((f) => ({ ...f, cost_out: parseFloat(e.target.value || "0") }))}
                data-testid="cb-cost-out"
              />
            </Field>
          </div>
          <button
            onClick={save}
            disabled={saving || !form.name.trim()}
            className="brut-btn text-sm disabled:opacity-40"
            data-testid="cb-save"
          >
            <Sparkles size={12} strokeWidth={3} /> {saving ? "Saving…" : "Save custom brain"}
          </button>
          {preset.supports_fine_tune && (
            <div className="text-xs text-slate-600 bg-amber-50 border-2 border-amber-300 rounded-lg p-3">
              <b>Bonus:</b> {preset.name} supports fine-tuning. After you save, you can launch a training job from
              <Link to="/studio/fine-tune" className="underline ml-1 font-bold">Fine-Tune Bridge</Link>.
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          className="brut-btn text-sm bg-white text-slate-900 disabled:opacity-40"
          data-testid="cb-prev"
        >
          <ArrowLeft size={12} strokeWidth={3} /> Back
        </button>
        {step < 4 && (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canAdvance}
            className="brut-btn text-sm disabled:opacity-40"
            data-testid="cb-next"
          >
            Next <ArrowRight size={12} strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-700 mb-1">
        {label}{required && <span className="text-red-500">*</span>}
      </div>
      {children}
    </div>
  );
}
